import React, { useState, useEffect } from "react";
import { NavLink } from "react-router-dom";
import logo from "../assets/logo.png";
import api from "../services/api";
import {
  LayoutDashboardIcon,
  StoreIcon,
  PackageIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  LandmarkIcon,
  UserCircleIcon,
  HelpCircleIcon,
  TagIcon,
  LogOutIcon,
  ShieldCheckIcon,
  BellIcon,
  TruckIcon,
  MailIcon,
  MobilePhoneIcon,
  XIcon
} from "./Icons";

function Sidebar({ user, onLogout, isOpen, onToggle }) {
  const [readiness, setReadiness] = useState(null);
  const [unreadNotifs, setUnreadNotifs] = useState(0);

  useEffect(() => {
    let isMounted = true;
    const loadVerificationStatus = async () => {
      try {
        const res = await api.get("/vendor/verification/status");
        if (isMounted && res.data) {
          setReadiness(res.data);
        }
      } catch (e) {
        // Silently handle
      }
    };
    const loadUnreadCount = async () => {
      try {
        const res = await api.get("/vendor/notifications");
        if (isMounted && res.data && typeof res.data.unreadCount === "number") {
          setUnreadNotifs(res.data.unreadCount);
        }
      } catch (e) {
        // Silently handle
      }
    };
    loadVerificationStatus();
    loadUnreadCount();
    const interval = setInterval(loadUnreadCount, 35000);
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const storeName = user?.storeName || (user?.firstName ? `${user.firstName}'s Store` : "My Store");
  const userInitials = (user?.firstName ? user.firstName.charAt(0) : "S") + (user?.lastName ? user.lastName.charAt(0) : "");
  
  const isFullyVerified = readiness?.isEligible || false;
  const progressPct = readiness?.requirements?.progressPercentage || 25;

  const handleLinkClick = () => {
    if (isOpen && onToggle) {
      onToggle();
    }
  };

  return (
    <>
      <div
        className={isOpen ? "sidebar-overlay open" : "sidebar-overlay"}
        onClick={onToggle}
      />
      <aside className={isOpen ? "app-sidebar open" : "app-sidebar"}>
        {/* ═══════════ TOP BRANDING & SELLER PROFILE HEADER ═══════════ */}
        <div className="sidebar-header">
          {/* 1. Logo & Brand */}
          <div className="sidebar-brand-wrapper">
            <img
              src={logo}
              alt="IM Online"
              className="sidebar-logo"
            />
            <div style={{ minWidth: 0, flex: 1 }}>
              <span className="sidebar-brand-main">
                IM ONLINE
              </span>
              <span className="sidebar-brand-sub">
                SELLER CENTER
              </span>
            </div>
            <button
              type="button"
              className="sidebar-mobile-close-btn"
              onClick={onToggle}
              aria-label="Close sidebar"
            >
              <XIcon size={16} color="#475569" />
            </button>
          </div>

          {/* 2. Seller Profile & Verification Card */}
          <div className="sidebar-seller-card">
            <div className="sidebar-seller-top">
              <div className="sidebar-seller-avatar">
                {storeName.charAt(0).toUpperCase()}
              </div>
              <div className="sidebar-seller-info">
                <strong
                  className="sidebar-seller-name"
                  title={storeName}
                >
                  {storeName}
                </strong>
                <span className="sidebar-seller-type">
                  {user?.businessType || "Merchant Store"}
                </span>
              </div>
            </div>

            {/* Verification Status Row */}
            <div className="sidebar-seller-status-row">
              {isFullyVerified ? (
                <span className="sidebar-verification-label verified">
                  <span className="sidebar-verification-dot verified" />
                  Verified Merchant
                </span>
              ) : (
                <>
                  <span className="sidebar-verification-label pending">
                    <span className="sidebar-verification-dot pending" />
                    Verification
                  </span>
                  <span style={{ fontSize: "11px", fontWeight: "800", color: "#b45309" }}>
                    {progressPct}%
                  </span>
                </>
              )}
            </div>

            {/* Compact Progress Indicator */}
            {!isFullyVerified && (
              <div className="sidebar-progress-track">
                <div
                  className="sidebar-progress-bar pending"
                  style={{ width: `${Math.max(8, Math.min(100, progressPct))}%` }}
                />
              </div>
            )}
          </div>
        </div>

        {/* ═══════════ NAVIGATION LINKS ═══════════ */}
        <nav className="sidebar-nav" style={{ padding: "14px 12px" }}>
          {/* Group 1: MY BUSINESS */}
          <p className="sidebar-section-label" style={{ fontSize: "10px", fontWeight: "800", color: "#8a94a6", letterSpacing: "1px", margin: "0 0 6px 8px" }}>
            MY BUSINESS
          </p>
          <NavLink to="/vendor/dashboard" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <LayoutDashboardIcon size={17} />
            </span>
            <span>Dashboard</span>
          </NavLink>
          <NavLink to="/vendor/store" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <StoreIcon size={17} />
            </span>
            <span>My Store</span>
          </NavLink>
          <NavLink to="/vendor/products" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <PackageIcon size={17} />
            </span>
            <span>Products</span>
          </NavLink>
          <NavLink to="/vendor/orders" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <ShoppingBagIcon size={17} />
            </span>
            <span>Orders</span>
          </NavLink>
          <NavLink to="/vendor/notifications" className="sidebar-link" activeClassName="active" onClick={handleLinkClick} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span className="sidebar-link-icon">
                <BellIcon size={17} />
              </span>
              <span>Notifications</span>
            </div>
            {unreadNotifs > 0 && (
              <span style={{ fontSize: "11px", fontWeight: "800", color: "#ffffff", background: "#ef4444", padding: "1px 7px", borderRadius: "10px", lineHeight: "1.4" }}>
                {unreadNotifs}
              </span>
            )}
          </NavLink>

          {/* Group 2: FULFILLMENT */}
          <p className="sidebar-section-label" style={{ fontSize: "10px", fontWeight: "800", color: "#8a94a6", letterSpacing: "1px", margin: "14px 0 6px 8px" }}>
            FULFILLMENT
          </p>
          <NavLink to="/vendor/pickup-requests" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <TruckIcon size={17} />
            </span>
            <span>Order Pickup Requests</span>
          </NavLink>

          {/* Group 3: GROWTH */}
          <p className="sidebar-section-label" style={{ fontSize: "10px", fontWeight: "800", color: "#8a94a6", letterSpacing: "1px", margin: "14px 0 6px 8px" }}>
            GROWTH
          </p>
          <NavLink to="/vendor/category-requests" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <TagIcon size={17} />
            </span>
            <span>Category Requests</span>
          </NavLink>
          <NavLink to="/vendor/subscription" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <CreditCardIcon size={17} />
            </span>
            <span>Subscription</span>
          </NavLink>

          {/* Group 3: COMPLIANCE & FINANCE */}
          <p className="sidebar-section-label" style={{ fontSize: "10px", fontWeight: "800", color: "#8a94a6", letterSpacing: "1px", margin: "14px 0 6px 8px" }}>
            COMPLIANCE &amp; FINANCE
          </p>
          <NavLink to="/vendor/verification" className="sidebar-link" activeClassName="active" onClick={handleLinkClick} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span className="sidebar-link-icon">
                <ShieldCheckIcon size={17} />
              </span>
              <span>Verification</span>
            </div>
            {isFullyVerified ? (
              <span style={{ fontSize: "10px", fontWeight: "800", color: "#166534", background: "#dcfce7", padding: "2px 6px", borderRadius: "6px" }}>
                ✓ Verified
              </span>
            ) : (
              <span style={{ fontSize: "10px", fontWeight: "800", color: "#b91c1c", background: "#fee2e2", padding: "2px 6px", borderRadius: "6px" }}>
                ● Action Required
              </span>
            )}
          </NavLink>
          <NavLink to="/vendor/bank" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <LandmarkIcon size={17} />
            </span>
            <span>Bank &amp; Payouts</span>
          </NavLink>

          {/* Group 4: ACCOUNT */}
          <p className="sidebar-section-label" style={{ fontSize: "10px", fontWeight: "800", color: "#8a94a6", letterSpacing: "1px", margin: "14px 0 6px 8px" }}>
            ACCOUNT
          </p>
          <NavLink to="/vendor/profile" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <UserCircleIcon size={17} />
            </span>
            <span>Profile</span>
          </NavLink>
          <NavLink to="/vendor/messages" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <MailIcon size={17} />
            </span>
            <span>Messages</span>
          </NavLink>
          <NavLink to="/vendor/support" className="sidebar-link" activeClassName="active" onClick={handleLinkClick}>
            <span className="sidebar-link-icon">
              <HelpCircleIcon size={17} />
            </span>
            <span>Help &amp; Support</span>
          </NavLink>
        </nav>

        {/* ═══════════ FOOTER USER CARD ═══════════ */}
        <div className="sidebar-footer" style={{ padding: "14px", borderTop: "1px solid #edf0f4", marginTop: "auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
            <div style={{
              width: "32px",
              height: "32px",
              borderRadius: "50%",
              background: "#111315",
              color: "#ffffff",
              display: "grid",
              placeItems: "center",
              fontSize: "11.5px",
              fontWeight: "800",
              flexShrink: 0
            }}>
              {userInitials}
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <span className="sidebar-user-name" style={{ fontSize: "12.5px", fontWeight: "800", color: "#111315", lineHeight: "1.2", display: "block" }}>
                {user.firstName} {user.lastName}
              </span>
              <span className="sidebar-user-email" style={{ fontSize: "10.5px", color: "var(--text-muted)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>
                {user.email}
              </span>
            </div>
          </div>

          <button
            type="button"
            onClick={() => {
              if (window.triggerPwaInstall) {
                window.triggerPwaInstall();
              }
            }}
            style={{
              width: "100%",
              padding: "7px 10px",
              marginBottom: "8px",
              borderRadius: "8px",
              background: "#fff5f5",
              border: "1px solid rgba(168, 80, 80, 0.25)",
              color: "#a85050",
              fontWeight: "750",
              fontSize: "11.5px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              cursor: "pointer",
            }}
          >
            <MobilePhoneIcon size={15} color="#a85050" />
            <span>Add App to Home Screen</span>
          </button>

          <button
            onClick={onLogout}
            style={{
              width: "100%",
              fontSize: "12.5px",
              padding: "8px 12px",
              fontWeight: "750",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "8px",
              background: "#fef2f2",
              border: "1.5px solid #fecaca",
              color: "#dc2626",
              borderRadius: "8px",
              cursor: "pointer",
              transition: "all 0.15s ease"
            }}
            title="Sign Out / Logout from Seller Panel"
          >
            <LogOutIcon size={16} color="#dc2626" />
            <span>Sign Out</span>
          </button>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
